import authService from './authService';
import { getApiUrl } from '@/config/apiConfig';

/**
 * 直播间绑定服务
 */
class BindingService {
  /**
   * 绑定直播间
   * @param {Object} bindingData 绑定数据
   * @param {string} bindingData.creatorAccount 用户账号（对应要绑定的 roomid）
   * @param {string} bindingData.platform 平台名称
   * @param {string} bindingData.verificationMethod 验证方法
   * @returns {Promise<Object>} 绑定结果
   */
  async bindCreator(bindingData) {
    try {
      // 开发环境也调用真实接口，方便测试
      console.log('开始绑定主播:', bindingData);
      
      // 开发环境模拟绑定成功（暂时保留，但添加调试信息）
      if (process.env.NODE_ENV === 'development') {
        console.log('开发环境：模拟绑定成功', bindingData);
        // 为了测试，我们暂时注释掉模拟数据，让开发环境也调用真实接口
        // return {
        //   success: true,
        //   message: '绑定成功',
        //   data: {
        //     bindingId: 'dev_binding_' + Date.now(),
        //     ...bindingData
        //   }
        // };
      }

      // 调用真实接口
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('用户未登录');
      }

      // 准备请求数据 - 根据新接口要求
      const requestBody = {
        creatorAccount: bindingData.creatorAccount, // creatorAccount 对应要绑定的 roomid
        platform: bindingData.platform || 'tiktok',
        verificationMethod: bindingData.verificationMethod || 'manual',
        bindingSource: 'web' // bindingSource 固定为 "web"
      };

      // 准备请求头
      const headers = {
        'Content-Type': 'application/json',
        'katoken': token,
        'req_app_id': this.getAppId(),
        'req_app_secret': this.getAppSecret(),
        'Accept': '*/*',
        'Connection': 'keep-alive'
      };

      const response = await fetch(getApiUrl('USER_CREATOR_BINDING_BIND'), {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`绑定请求失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('绑定API返回结果:', result);
      
      // 根据新的API响应格式处理结果
      // 如果success为true，表示绑定申请已成功提交（即使需要审核）
      return {
        success: result.success === true,
        message: result.message || '绑定申请已提交',
        data: result.data,
        errCode: result.errCode,
        errMessage: result.errMessage
      };
    } catch (error) {
      console.error('绑定直播间失败:', error);
      return {
        success: false,
        message: error.message || '绑定失败，请稍后重试',
        data: null
      };
    }
  }

  /**
   * 获取应用ID
   * @returns {string} 应用ID
   */
  getAppId() {
    // 使用与其他接口相同的应用ID
    return 'LivePlugin';
  }

  /**
   * 获取应用密钥
   * @returns {string} 应用密钥
   */
  getAppSecret() {
    // 使用与其他接口相同的应用密钥
    return 'ewMpjg7t4vX5bK13oRZzSB8mEuNGr2Yy';
  }

  /**
   * 检查绑定状态
   * @returns {Promise<Object>} 绑定状态
   */
  async checkBindingStatus() {
    try {
      // 开发环境模拟已绑定状态
      if (process.env.NODE_ENV === 'development') {
        console.log('开发环境：模拟绑定状态检查');
        return {
          isBound: true,
          bindingData: {
            nickname: 'Olivia_Streams',
            platform: 'tiktok',
            bindingSource: '123456789',
            boundAt: new Date().toISOString()
          }
        };
      }

      const token = authService.getToken();
      
      if (!token) {
        return { isBound: false, bindingData: null };
      }

      const headers = {
        'katoken': token,
        'req_app_id': this.getAppId(),
        'req_app_secret': this.getAppSecret(),
        'Accept': '*/*',
        'Connection': 'keep-alive'
      };

      const response = await fetch(getApiUrl('USER_CREATOR_BINDING_STATUS'), {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        return { isBound: false, bindingData: null };
      }

      const result = await response.json();
      
      return {
        isBound: result.code === 200 && result.data && result.data.isBound,
        bindingData: result.data
      };
    } catch (error) {
      console.error('检查绑定状态失败:', error);
      return { isBound: false, bindingData: null };
    }
  }

  /**
   * 解绑直播间
   * @returns {Promise<Object>} 解绑结果
   */
  async unbindCreator() {
    try {
      // 开发环境模拟解绑成功
      if (process.env.NODE_ENV === 'development') {
        console.log('开发环境：模拟解绑成功');
        return {
          success: true,
          message: '解绑成功'
        };
      }

      const token = authService.getToken();
      
      if (!token) {
        throw new Error('用户未登录');
      }

      const headers = {
        'katoken': token,
        'req_app_id': this.getAppId(),
        'req_app_secret': this.getAppSecret(),
        'Accept': '*/*',
        'Connection': 'keep-alive'
      };

      const response = await fetch(getApiUrl('USER_CREATOR_BINDING_UNBIND'), {
        method: 'POST',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`解绑请求失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: result.code === 200,
        message: result.message || '解绑成功'
      };
    } catch (error) {
      console.error('解绑直播间失败:', error);
      return {
        success: false,
        message: error.message || '解绑失败，请稍后重试'
      };
    }
  }
}

export default new BindingService();
